import type { Role } from '@/api/auth';
import type { CuratorCardEnrollment } from '@/api/curatorCard';

/**
 * Решения карточки куратора в админке — без разметки (28.09.2026, договор `curator-card.md`).
 *
 * ⚠️ КУРАТОРА ДАЁТ ГРУППА. У записи в группе «сменить куратора» — это перевести ученика в другую группу того же
 * курса, «снять» — вывести из группы. Поле «куратор записи» живо только у записи без группы (LEGACY, D25): там
 * «сменить» — `assign-curator`, а «снять» сервер не умеет — решение владельца 28.09.2026 новую ручку не заводить.
 *
 * Все ручки действий — только admin (`group_handler.go`, `enrollment_handler.go` adminWrite): менеджеру кнопки
 * пообещали бы 403.
 */

export type CuratorRowAction = 'moveGroup' | 'removeFromGroup' | 'changeCurator';

export function curatorRowActions(e: Pick<CuratorCardEnrollment, 'group_id'>, role: Role | undefined): CuratorRowAction[] {
  if (role !== 'admin') return [];
  return e.group_id ? ['moveGroup', 'removeFromGroup'] : ['changeCurator'];
}

/** Куда можно назначить запись этому куратору: его группы этого курса и «лично» — только записи без группы. */
export type AssignTarget = { kind: 'group'; groupId: string; name: string } | { kind: 'personal' };

export function assignTargets(
  enrollment: { status: string; group_id?: string | null },
  groups: readonly { id: string; name: string; curators: { id: string }[] }[],
  curatorId: string,
): AssignTarget[] {
  if (enrollment.status !== 'active') return [];
  const out: AssignTarget[] = groups
    .filter((g) => g.id !== enrollment.group_id && g.curators.some((c) => c.id === curatorId))
    .map((g) => ({ kind: 'group' as const, groupId: g.id, name: g.name }));
  // Лично — только без группы: у ученика в группе сервер ответит 409 student_in_group (canAssignCurator).
  if (!enrollment.group_id) out.push({ kind: 'personal' });
  return out;
}

/**
 * Средний срок ответа словом: до двух суток — часами, дальше — днями. null — проверок за 30 дней не было
 * (не «0 ч»: ноль часов — это ложь о скорости).
 */
export function avgResponse(hours: number | null | undefined): { key: string; n: number } | null {
  if (hours == null || !Number.isFinite(hours)) return null;
  if (hours < 48) return { key: 'adminCuratorCard.avgHours', n: Math.max(0, Math.round(hours * 10) / 10) };
  return { key: 'adminCuratorCard.avgDays', n: Math.round((hours / 24) * 10) / 10 };
}

/** Решение по домашке словом. */
const HOMEWORK_STATUS_KEYS: Record<string, string> = {
  approved: 'adminCuratorCard.hwApproved',
  revision: 'adminCuratorCard.hwRevision',
  rejected: 'adminCuratorCard.hwRejected',
};

export function homeworkStatusKey(status: string | null | undefined): string {
  return (status && HOMEWORK_STATUS_KEYS[status]) || 'adminCuratorCard.hwOther';
}

/**
 * Какие блоки карточки `/admin/students/:id` рисовать по роли открытой учётки.
 *
 * У куратора нет ученических блоков (решение владельца 28.09.2026): прогресс, расход и «Доступ к курсам» — про того,
 * кто учится; вместо них — его ученики, сводка и лента проверок.
 */
export type DetailSection =
  | 'progressLink'
  | 'points'
  | 'devices'
  | 'curatorSubtypes'
  | 'curatorCard'
  | 'enrollmentSummary'
  | 'transfers'
  | 'usage'
  | 'access';

export function detailSections(role: Role | undefined): DetailSection[] {
  if (role === 'curator') return ['curatorSubtypes', 'curatorCard'];
  const out: DetailSection[] = ['progressLink'];
  if (role === 'student') out.push('enrollmentSummary', 'transfers', 'points', 'devices');
  out.push('usage', 'access');
  return out;
}
